import Typography from '@mui/material/Typography';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection';
import { Table, TableName } from '@openx/components-table/Table';
import type { OrganizationApiKeyData } from '@openx/types/organization';
import {
  type AllTableCriteria,
  type CriteriaChange,
  CriteriaDimension,
  SortDirection,
} from '@openx/types/tableCriteria';
import type { RowActionProps } from '@openx/types/tableHelpers';
import { clientSideCriteriaProcessing } from '@openx/utils/lib/clientSideCriteriaProcessing';
import { applyCriteriaChange } from '@openx/utils/lib/tableCriteria';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganizationContext } from '../../../utils/context/OrganizationContext';
import { useConfirmationModal } from '../hooks/useConfirmationModal';
import { useGetTableColumns } from '../hooks/useGetTableColumns';

type ApiKeysSectionProps = {
  organizationId: string;
};

const initialCriteria: AllTableCriteria = {
  [CriteriaDimension.SORT]: {
    column: 'created_date',
    direction: SortDirection.DESC,
    sortableColumns: ['first_name', 'expires_at', 'created_date'],
  },
};

export const ApiKeysSection = ({ organizationId }: ApiKeysSectionProps) => {
  const { t } = useTranslation();
  const { apiKeysTableColumns } = useGetTableColumns();
  const [criteria, setCriteria] = useState<AllTableCriteria>(initialCriteria);
  const { useOrganizationApiKeysFetch } = useOrganizationContext();
  const { apiKeys, loading } = useOrganizationApiKeysFetch(organizationId);
  const handleDeleteApiKey = useConfirmationModal();

  const handleCriteriaChange = useCallback(
    (newCriteria: CriteriaChange<CriteriaDimension>) => {
      const newCriteriaProcessed = applyCriteriaChange(criteria, newCriteria);
      setCriteria(newCriteriaProcessed);
    },
    [criteria]
  );

  const mappedApiKeys = useMemo(() => {
    return clientSideCriteriaProcessing(apiKeys ?? [], criteria);
  }, [apiKeys, criteria]);

  const rowAction: RowActionProps<OrganizationApiKeyData> = useMemo(
    () => ({
      label: 'delete',
      'data-test': 'delete-api-key',
      onClick: ({ id, first_name }) => handleDeleteApiKey(id, first_name),
    }),
    [handleDeleteApiKey]
  );

  return (
    <FormSection title={t('API Keys')}>
      {!mappedApiKeys?.length && !loading ? (
        <Typography data-test="no-api-keys-message" variant="body2" color={'textSecondary'}>
          {t('No API Keys were generated.')}
        </Typography>
      ) : (
        <Table
          name={TableName.API_KEYS}
          data={mappedApiKeys}
          onCriteriaChange={handleCriteriaChange}
          dangerRowAction={rowAction}
          criteria={criteria}
          columns={apiKeysTableColumns}
          loading={loading}
          enableTableHeaderCustomization={false}
        />
      )}
    </FormSection>
  );
};
